import { Tabs } from '@mantine/core';
import cs from 'classnames';
import dynamic from 'next/dynamic';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import useIsLandscape from '@/DesignSystem/Hook/useIsLandscape';
import { useLoggedIn } from '@/Module/Auth/Hook';
import { getExamByContentIdAction } from '@/Module/Exam/Store/Action';
import { selectedLessonSectionSelector } from '@/Module/Lesson/Store/Selector/selectedLessonSectionSelector';
import { lessonSectionSetActiveTabNumber } from '@/Module/Lesson/Store/Slice/LessonSectionSlice';
import { useAppDispatch, useAppSelector } from '@/Module/Store/Hook';
import style from './lessonDetailTab.module.scss';
const LessonDetailPDF = dynamic(() => import('./LessonDetailPDF'), {
  ssr: false
});
const LessonDetailPlayer = dynamic(() => import('./LessonDetailPlayer'), {
  ssr: false
});
const LessonDetailExam = dynamic(() => import('./LessonDetailExam'), {
  ssr: false
});
const LessonDetailTab: FC = () => {
  const dispatch = useAppDispatch();
  const loggedIn = useLoggedIn();
  const firstTabRef = useRef<HTMLButtonElement>(null);
  const hasHandOut = useAppSelector(state => selectedLessonSectionSelector(state)?.hasHandOut);
  const hasExam = useAppSelector(state => state.exam.exam?.isActive);
  const activeTabNumber = useAppSelector(state => state.lessonSection.activeTabNumber);
  const videoId = useAppSelector(state => selectedLessonSectionSelector(state)?.videoId);
  const isLandscape = useIsLandscape();
  useEffect(() => {
    if (!firstTabRef.current) return;
    if (activeTabNumber === 1) firstTabRef.current.click();
  }, [activeTabNumber]);
  useEffect(() => {
    if (!(videoId && loggedIn)) return () => {};
    const promise = dispatch(getExamByContentIdAction(videoId));
    return () => {
      promise.abort();
    };
  }, [videoId]);
  const tabs: ReactNode[] = useMemo(() => {
    const result = [<Tabs.Tab key="ویدیو" value="1" ref={firstTabRef}>
        ویدیو
      </Tabs.Tab>];
    if (hasHandOut) {
      result.push(<Tabs.Tab key="نکات مهم درس" value="2">
          نکات مهم درسی
        </Tabs.Tab>);
    }
    if (hasExam) {
      result.push(<Tabs.Tab key="آزمون تثبیت" value="3">
          آزمون تثبیت
        </Tabs.Tab>);
    }
    return result;
  }, [activeTabNumber, hasHandOut, hasExam]);
  const panel: ReactNode[] = useMemo(() => {
    const result: ReactNode[] = [<Tabs.Panel key="1" value="1">
        <LessonDetailPlayer />
      </Tabs.Panel>];
    if (hasHandOut) {
      result.push(<Tabs.Panel value="2" key="نکات مهم درسی">
          <LessonDetailPDF />
        </Tabs.Panel>);
    }
    if (hasExam) {
      result.push(<Tabs.Panel value="3" key="آزمون تثبیت">
          <LessonDetailExam />
        </Tabs.Panel>);
    }
    return result;
  }, [hasHandOut, hasExam]);
  return <div className={cs(isLandscape && 'order-[-2] mt-2')} data-sentry-component="LessonDetailTab" data-sentry-source-file="LessonDetailTab.tsx">
      <Tabs defaultValue={String(activeTabNumber)} onChange={value => dispatch(lessonSectionSetActiveTabNumber(Number(value)))} classNames={style} data-sentry-element="Tabs" data-sentry-source-file="LessonDetailTab.tsx">
        <Tabs.List className={cs(isLandscape && '!mb-0 self-center', 'my-2 sm:mb-2 sm:mt-0 lg:mt-2')} data-sentry-element="unknown" data-sentry-source-file="LessonDetailTab.tsx">{tabs}</Tabs.List>
        {panel}
      </Tabs>
    </div>;
};
export default LessonDetailTab;